import React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface WidgetCardProps {
  title: string;
  icon: React.ReactNode;
  colSpan?: 1 | 2 | 3;
  action?: React.ReactNode;
  children: React.ReactNode;
  isLoading?: boolean;
  error?: string | null;
  className?: string;
}

function WidgetLoadingSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="h-8 bg-gray-200 rounded w-full mt-4"></div>
      <div className="h-8 bg-gray-200 rounded w-full"></div>
    </div>
  );
}

function WidgetError({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 p-3 rounded-lg text-sm">
      <AlertCircle className="w-4 h-4 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

const colSpanClasses = {
  1: '',
  2: 'md:col-span-2',
  3: 'md:col-span-2 lg:col-span-3',
};

export default function WidgetCard({
  title,
  icon,
  colSpan = 1,
  action,
  children,
  isLoading = false,
  error = null,
  className,
}: WidgetCardProps) {
  return (
    <div
      className={cn(
        'bg-white rounded-xl shadow-sm border border-gray-200 p-6',
        colSpanClasses[colSpan],
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="text-blue-600">{icon}</div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        {action && <div>{action}</div>}
      </div>

      {/* Content */}
      {isLoading ? (
        <WidgetLoadingSkeleton />
      ) : error ? (
        <WidgetError message={error} />
      ) : (
        children
      )}
    </div>
  );
}
